/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Shared hook for checking if authentication and organization are ready
 *
 * This hook provides a consistent way to check if the app is ready to make
 * authenticated API calls. Use this in mutation hooks and form components
 * to prevent premature API calls.
 */

import { useOrganization } from "@/contexts/organization-context";
import { useAuthSafe } from "@/hooks/auth/use-auth-safe";

export interface AuthReadyState {
  /** Whether authentication is fully loaded and user is signed in */
  isAuthReady: boolean;
  /** Whether organization context is ready */
  isOrgReady: boolean;
  /** Whether both auth and org are ready (can make API calls) */
  isReady: boolean;
  /** Detailed readiness information for debugging */
  details: {
    authLoaded: boolean;
    isSignedIn: boolean;
    hasOrganizationId: boolean;
    isOrganizationLoading: boolean;
    isClientReady: boolean;
  };
}

/**
 * Hook to check if authentication and organization are ready
 *
 * @returns AuthReadyState with readiness flags and details
 *
 * @example
 * ```tsx
 * const { isReady, isAuthReady, isOrgReady } = useAuthReady();
 *
 * const createMutation = useMutation({
 *   mutationFn: async (data) => {
 *     if (!isReady) {
 *       throw new Error("Authentication not ready");
 *     }
 *     return api.post("/api/resource", data);
 *   }
 * });
 * ```
 */
export function useAuthReady(): AuthReadyState {
  const { isLoaded: authLoaded, isSignedIn } = useAuthSafe();
  const {
    organizationId,
    isLoading: isOrganizationLoading,
    isClientReady,
  } = useOrganization();

  const isAuthReady = authLoaded && isSignedIn;
  const isOrgReady =
    !isOrganizationLoading && isClientReady && !!organizationId;
  const isReady = isAuthReady && isOrgReady;

  return {
    isAuthReady,
    isOrgReady,
    isReady,
    details: {
      authLoaded: authLoaded ?? false,
      isSignedIn: isSignedIn ?? false,
      hasOrganizationId: !!organizationId,
      isOrganizationLoading,
      isClientReady,
    },
  };
}
